import * as React from 'react';

import {DraggableWrapper} from '@spaced-out/ui-design-system/lib/components/DraggableWrapper';
import {useSortable} from '@dnd-kit/react/sortable';

import {classify} from 'src/utils/classify';

import {CanvasTile} from 'src/components/analytics-genesis/byod/pages/dashboard-builder/canvas/canvas-tile';
import type {DragType} from './dnd-config';
import layoutCss from './dashboard-builder-canvas-layout.css';


export type SortableTileProps = {
  tileId: string;
  colIndex: number;
  canEdit: boolean;
  dropType: DragType;
  rowId: string;
  wrapperClass: string;
  /**
   * Widget display name surfaced to screen readers via the accessibility
   * plugin's `resolveDragSourceLabel`. Without it announcements degrade to
   * the generic word "tile."
   */
  label: string;
  /**
   * True when an active drag is from a different row and this row is at
   * its tile-count cap. Disables this sortable as a drop target so no
   * indicator / optimistic-sort animation suggests the row will accept a
   * tile it can't.
   */
  disableAsTarget: boolean;
  onEdit: (tileId: string) => void;
  onRemove: (tileId: string) => void;
};

/**
 * Single sortable tile wrapped for `@dnd-kit/react`. The `data-byod-tile`
 * attribute carries the tile id so two consumers can find it from the DOM
 * without prop-drilling refs:
 *   - `tile-row.tsx` measures sibling tile rects to position the drop
 *     indicator between actual tile edges (via `hasAttribute` filter).
 *   - `use-byod-keyboard-nav.ts` resolves a target tile by id when the
 *     user moves the keyboard cursor (via attribute-equals selector).
 */
export function SortableTile({
  tileId,
  colIndex,
  canEdit,
  dropType,
  rowId,
  wrapperClass,
  label,
  disableAsTarget,
  onEdit,
  onRemove,
}: SortableTileProps) {
  const {ref, isDragSource} = useSortable({
    id: tileId,
    index: colIndex,
    group: rowId,
    type: dropType,
    // When the row is at capacity for incoming cross-row drops, set accept
    // to a non-matching sentinel so this tile won't register as a drop
    // target — but remains draggable (the user can still drag it out).
    accept: disableAsTarget ? '__none__' : dropType,
    disabled: !canEdit,
    data: {type: dropType, rowId, label},
    // Default sortable plugins (OptimisticSortingPlugin,
    // SortableKeyboardPlugin) shift sibling tiles to make room during
    // drag — we render the source as a stationary gray ghost via CSS
    // instead, so plugin-driven movement is unwanted noise here.
    plugins: [],
  });

  return (
    <DraggableWrapper
      ref={ref as React.Ref<HTMLDivElement>}
      enableNativeDrag={false}
      isDragging={false}
      wrapperProps={
        {
          'data-byod-tile': tileId,
          ...(!canEdit && {tabIndex: 0, 'aria-label': label}),
        } as unknown as React.HTMLAttributes<HTMLDivElement> &
          Record<string, string>
      }
      classNames={{
        wrapper: classify(
          wrapperClass,
          isDragSource && layoutCss.tileDraggableFillDraggingSource,
        ),
        dragHandle: canEdit
          ? layoutCss.tileDraggableHandleHoverReveal
          : layoutCss.tileDraggableHandleHidden,
      }}
    >
      <CanvasTile
        tileId={tileId}
        canEdit={canEdit}
        isRemoving={false}
        onEdit={onEdit}
        onRemove={onRemove}
      />
    </DraggableWrapper>
  );
}
